import SearchForm from "@shared/components/SearchForm";
import theme from "@shared/utils/themeColors";
import { InfiniteData, useInfiniteQuery } from "@tanstack/react-query";
import React, { useState } from "react";
import { getProfileThumbnail } from "../../../shared/hooks/useProfile";
import { fetchUserConnections } from "../services/connections.service";
import { ConnectionsResponse } from "../types/connection.types";

interface MembersSidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  onSelectUser: (userId: string) => void;
  selectedUserId?: string;
}

export const MembersSidebar: React.FC<MembersSidebarProps> = ({
  sidebarOpen,
  setSidebarOpen,
  onSelectUser,
  selectedUserId,
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteQuery<
      ConnectionsResponse,
      Error,
      InfiniteData<ConnectionsResponse>,
      string[],
      number
    >({
      queryKey: ["connections", searchTerm],
      queryFn: async ({ pageParam }) => {
        const response = await fetchUserConnections({
          page: pageParam,
          search: searchTerm,
        });
        return response;
      },
      getNextPageParam: (lastPage) =>
        lastPage.hasNextPage ? lastPage.currentPage + 1 : undefined,
      initialPageParam: 1,
      staleTime: 0,
      refetchOnWindowFocus: true,
    });

  const connections = data?.pages.flatMap((page) => page.connections) || [];

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  return (
    <div
      id="profile-sidebar"
      className={`absolute z-20 top-0 bottom-0 w-full md:w-auto md:static md:top-auto md:bottom-auto -mr-px md:translate-x-0 transition-transform duration-200 ease-in-out ${
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      }`}
    >
      <div className="sticky top-16 bg-white dark:bg-gray-900 overflow-x-hidden overflow-y-auto no-scrollbar shrink-0 border-r border-gray-200 dark:border-gray-700 md:w-72 xl:w-80 h-[calc(100vh-64px)]">
        {/* Search form */}
        <div className="sticky top-0 p-4 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
          <SearchForm
            placeholder="Search connections..."
            onChange={handleSearch}
          />
        </div>

        <div className="py-4 px-4">
          {/* Connections list */}
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-100"></div>
            </div>
          ) : connections.length === 0 ? (
            <div className="text-center py-8">
              <p className={theme.text.muted}>No connections found</p>
            </div>
          ) : (
            <>
              <div className="mb-4">
                <div className={`text-xs font-semibold ${theme.text.muted}`}>
                  Connections
                </div>
                {connections.map((connection) => (
                  <button
                    key={connection.id}
                    className={`w-full text-left py-2 px-3 mb-0.5 rounded-lg ${
                      selectedUserId === connection.id
                        ? "${theme.bg.brand}/30 dark:${theme.bg.brand}/40"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800"
                    }`}
                    onClick={() => {
                      onSelectUser(connection.id);
                      setSidebarOpen(false);
                    }}
                  >
                    <div className="flex items-center">
                      <img
                        className="w-8 h-8 rounded-full mr-2"
                        src={getProfileThumbnail(connection)}
                        width="32"
                        height="32"
                        alt={connection.name}
                      />
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {connection.name}
                        </h4>
                        {connection.title && (
                          <div className={`text-xs ${theme.text.muted}`}>
                            {connection.title}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {/* Load more button */}
              {hasNextPage && (
                <div className="text-center">
                  <button
                    className={`btn-sm bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 ${theme.text.brand}`}
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                  >
                    {isFetchingNextPage ? "Loading..." : "Load More"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
